import { useState } from "react";
import {
  Box,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  type TypographyProps,
} from "@schaeffler-commerce/design-system";
import { Link } from "~/common/components/Link";
import { useTranslation } from "react-i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { FontSize, getFontStyle } from "@schaeffler-commerce/design-system";
import { C_SURFACE } from "@schaeffler-commerce/design-system";
import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import * as PriceUtils from "~/utils/PriceUtils";
import type { OrderData } from "~/domains/orders/types/modelsV2";

import Tag from "~/common/components/Tag/Tag";
import { lineClamp } from "~/common/components/icon/FontUtils";

export enum TableType {
  ORDERS = "ORDERS",
  QUOTES = "QUOTES",
}
interface OrderTableProps {
  type: TableType.ORDERS | TableType.QUOTES;
  data: OrderData[];
}

export default function OrderTable({ type, data }: OrderTableProps) {
  const { t } = useTranslation();
  const rowsPerPage = 5;
  const [page, setPage] = useState(0);

  const totalPages = Math.ceil(data.length / rowsPerPage);

  const currentRows = data.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  if (data.length == 0) {
    return null;
  }

  return (
    <>
      <Table sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <HeaderCell>
              <Typography variant="labelMedium">
                {type === TableType.QUOTES
                  ? t("quoteListPage.table.header.quoteNumber")
                  : t("homepage.recentOrderTH.poNumber")}
              </Typography>
            </HeaderCell>
            <HeaderCell>
              <TruncatedTypography variant="labelMedium">
                {type === TableType.QUOTES
                  ? t("quoteListPage.table.header.customerReferences")
                  : t("homepage.recentOrderTH.soNumber")}
              </TruncatedTypography>
            </HeaderCell>
            <HeaderCell sx={{ width: "114px" }}>
              <Typography variant="labelMedium">{t("homepage.recentOrderTH.dateEntered")}</Typography>
            </HeaderCell>
            <HeaderCell sx={{ width: "136px" }}>
              <Typography variant="labelMedium">
                {type === TableType.QUOTES ? t("homepage.recentOrderTH.status") : t("homepage.recentOrderTH.netPrice")}
              </Typography>
            </HeaderCell>
            <HeaderCell sx={{ width: "40px" }}></HeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {currentRows.map((item, index) => (
            <StyledTableRow key={index}>
              <StyledCell>
                <TruncatedTypography component={"b"}>{item.poNumber}</TruncatedTypography>
              </StyledCell>
              <StyledCell>
                <TruncatedTypography>{item.soNumber}</TruncatedTypography>
              </StyledCell>
              <StyledCell>
                <Typography>{formatNullableDate(item.datePlaced, DateFormatType.DATE)}</Typography>
              </StyledCell>
              <StyledCell>
                {type === TableType.QUOTES ? (
                  <StatusTag>{t(item.orderStatus)}</StatusTag>
                ) : (
                  <Typography component={"b"}>{PriceUtils.text(item.totalPrice)}</Typography>
                )}
              </StyledCell>
              <StyledCell>
                {!item.soNumber || item.soNumber == "-" ? (
                  <Tooltip placement="top" title={t("homepage.recentOrderTable.tooltip")} variant="simple">
                    <ArrowForwardIcon color="disabled" />
                  </Tooltip>
                ) : (
                  <Link href={item.detailsUrl}>
                    <ArrowForwardIcon color="primary" />
                  </Link>
                )}
              </StyledCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>

      <Stack
        direction="row"
        sx={{
          justifyContent: "center",
          gap: 1,
          marginY: 1
        }}>
        {Array.from({ length: totalPages }).map((_, idx) => (
          <PaginationDot
            key={`page-control-${idx}`}
            active={idx === page}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setPage(idx);
            }}
          />
        ))}
      </Stack>
    </>
  );
}

const PaginationDot = styled(Box, { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>(
  ({ theme, active }) => ({
    width: active ? 20 : 8,
    height: 8,
    borderRadius: active ? "10px" : "50%",
    background: active ? theme.palette.primary.main : theme.palette.divider,
    cursor: active ? "default" : "pointer",
    "&:hover": {
      background: theme.palette.primary.dark,
    },
  }),
);

const StatusTag = styled(Tag)({
  justifySelf: "flex-start",
});

const StyledTableRow = styled(TableRow)({
  backgroundColor: `white !important`,
  transition: "background-color 0.3s ease-in-out",
  "&:hover": {
    backgroundColor: `${C_SURFACE} !important`,
    cursor: "pointer",
  },
});

const StyledCell = styled(TableCell)({
  verticalAlign: "middle !important",
});

const HeaderCell = styled(StyledCell)({
  padding: "8px !important",
  "> label": {
    ...getFontStyle(FontSize.LabelMedium),
    fontWeight: "bold",
  },
});

const TruncatedTypography = styled(Typography)<TypographyProps>({
  ...lineClamp(1),
});
